import { apiClient, HttpError } from '@littlebird/auth-client';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

export const EXTENDED_NOTES_DIRECTORY = '/mnt/assistant-notes';

export const CatalogueEntrySchema = z.object({
  path: z.string(),
  kind: z.enum(['file', 'directory']),
  description: z.string(),
  size_bytes: z.number(),
});

export type CatalogueEntry = z.infer<typeof CatalogueEntrySchema>;

export const AssistantFileSchema = z.object({
  path: z.string(),
  text: z.string(),
  description: z.string(),
  size_bytes: z.number(),
  updated_at: z.string(),
});

export type AssistantFile = z.infer<typeof AssistantFileSchema>;

export const ASSISTANT_FILES_KEYS = {
  all: ['assistant-files'] as const,
  catalogue: () => [...ASSISTANT_FILES_KEYS.all, 'catalogue'] as const,
  file: (path: string) => [...ASSISTANT_FILES_KEYS.all, 'file', path] as const,
};

const CATALOGUE_STALE_TIME_MS = 15_000;

export const getAssistantFilesCatalogueQuery = () =>
  queryOptions({
    queryKey: ASSISTANT_FILES_KEYS.catalogue(),
    staleTime: CATALOGUE_STALE_TIME_MS,
    refetchOnMount: true,
    retry: 3,
    queryFn: async ({ signal }) => {
      try {
        const data = await apiClient.get('assistant_notes/vfs/catalogue', {
          signal,
        });
        return z.array(CatalogueEntrySchema).parse(data);
      } catch (error) {
        throw error instanceof HttpError
          ? new Error('Failed to fetch assistant files catalogue')
          : error;
      }
    },
  });

export const getAssistantFileQuery = (path: string | null) =>
  queryOptions({
    queryKey: ASSISTANT_FILES_KEYS.file(path ?? ''),
    enabled: !!path,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 3,
    queryFn: async ({ signal }) => {
      if (!path) {
        throw new Error('No file path selected');
      }
      try {
        const url = `assistant_notes/vfs/file?path=${encodeURIComponent(path)}`;
        const data = await apiClient.get(url, { signal });
        return AssistantFileSchema.parse(data);
      } catch (error) {
        if (error instanceof HttpError && error.status === 404) {
          throw new Error('File not found');
        }
        throw error instanceof HttpError
          ? new Error('Failed to fetch assistant file')
          : error;
      }
    },
  });

/** The user's global Assistant Notes store */
export const GLOBAL_ASSISTANT_FILES_SOURCE = {
  rootPath: EXTENDED_NOTES_DIRECTORY,
  catalogueQuery: getAssistantFilesCatalogueQuery(),
  getFileQuery: getAssistantFileQuery,
};
